// src/cockpit4/Workspace4.tsx
// ============================================
// Cockpit 4.0 - Workspace 4.0 主容器
//
// 设计理念：
//   - Workspace 化：9 个独立工作区，顶部 Tab 切换
//   - Hermes 中心化：右侧抽屉，任何 Workspace 都可呼出
//   - Live 化：实时数据流 + 实时活动
//
// 布局：
//   ┌─────────────────────────────────────────────┐
//   │              WorkspaceHeader4               │
//   ├─────────────────────────────────┬───────────┤
//   │                                 │           │
//   │      当前 Workspace 内容         │  Hermes   │
//   │      (全屏，不滚动)              │  Drawer   │
//   │                                 │  (可收起)  │
//   │                                 │           │
//   ├─────────────────────────────────┴───────────┤
//   │                StatusBar4                   │
//   └─────────────────────────────────────────────┘
//
// 视觉：暗色渐变背景 + 玻璃拟态 + 霓虹强调色
// ============================================

import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { useCockpit4Store, type WorkspaceId } from "./store";
// v6.39 游客全览：游客可浏览全部工作区（AI 超市开放 / 财务设置加密 / 其余毛玻璃预览遮罩）
import { useUserStore } from "../stores/userStore";
import WorkspaceHeader4 from "./WorkspaceHeader4";
import StatusBar4 from "./StatusBar4";
import WorkspaceBizCrumb from "./components/WorkspaceBizCrumb";
import HermesDrawer from "./HermesDrawer";
// v6.39 游客数据保护组件：毛玻璃预览遮罩 + 财务/设置加密锁
import GuestPreviewOverlay from "./components/GuestPreviewOverlay";
import WorkspaceAccessGate from "./components/WorkspaceAccessGate";
import ErrorBoundary from "./components/ErrorBoundary";
import AILauncherPanel from "./components/AILauncherPanel";
// Phase3 P1-2：首次登录新手向导
import FirstRunOnboarding from "./components/FirstRunOnboarding";
import CEOWorkspace from "./workspaces/CEOWorkspace";
import MarketingWorkspace from "./workspaces/MarketingWorkspace";
import CRMWorkspace from "./workspaces/CRMWorkspace";
import KnowledgeWorkspace from "./workspaces/KnowledgeWorkspace";
import ProjectsWorkspace from "./workspaces/ProjectsWorkspace";
import FinanceWorkspace from "./workspaces/FinanceWorkspace";
import WorkflowWorkspace from "./workspaces/WorkflowWorkspace";
import SettingsWorkspace from "./workspaces/SettingsWorkspace";
import MarketingStudioWorkspace from "./workspaces/MarketingStudioWorkspace";
import GeoDSOWorkspace from "./workspaces/GeoDSOWorkspace";
import BossViewWorkspace from "./workspaces/BossViewWorkspace";
import MediaWorkbench from "./workspaces/MediaWorkbench";
import RuntimeManager from "../runtime/RuntimeManager";
import { RuntimeName } from "../types/runtime";
import { KnowledgeQuery } from "../runtime/KnowledgeRuntime/types";

// ============================================
// Workspace 路由表
// ============================================

const WORKSPACES: Record<WorkspaceId, React.ComponentType> = {
  ceo: CEOWorkspace,
  marketing: MarketingWorkspace,
  crm: CRMWorkspace,
  knowledge: KnowledgeWorkspace,
  projects: ProjectsWorkspace,
  finance: FinanceWorkspace,
  workflow: WorkflowWorkspace,
  settings: SettingsWorkspace,
  media_studio: MarketingStudioWorkspace,
  geodso: GeoDSOWorkspace,
  boss_view: BossViewWorkspace,
  media_workbench: MediaWorkbench,
};

// ============================================
// 背景动效层（与 Cockpit 3.0 保持视觉延续）
// ============================================

function BackgroundEffects() {
  return (
    <div className="pointer-events-none fixed inset-0 overflow-hidden -z-10">
      {/* 渐变底色（#0B1220 → #1A2332） */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 20% 0%, rgba(59,130,246,0.08) 0%, transparent 50%)," +
            "radial-gradient(ellipse at 80% 100%, rgba(168,85,247,0.06) 0%, transparent 50%)," +
            "linear-gradient(180deg, #0B1220 0%, #1A2332 100%)",
        }}
      />
      {/* 网格线 */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px)," +
            "linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />
      {/* 扫描线 */}
      <motion.div
        className="absolute left-0 right-0 h-px"
        style={{
          background:
            "linear-gradient(90deg, transparent, rgba(59,130,246,0.4), transparent)",
          boxShadow: "0 0 12px rgba(59,130,246,0.6)",
        }}
        initial={{ top: "0%" }}
        animate={{ top: ["0%", "100%"] }}
        transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
      />
      {/* 角落光晕：左上 */}
      <div
        className="absolute -top-40 -left-40 w-96 h-96 rounded-full"
        style={{
          background: "radial-gradient(circle, rgba(59,130,246,0.15) 0%, transparent 70%)",
          filter: "blur(40px)",
        }}
      />
      {/* 角落光晕：右下 */}
      <div
        className="absolute -bottom-40 -right-40 w-96 h-96 rounded-full"
        style={{
          background: "radial-gradient(circle, rgba(168,85,247,0.12) 0%, transparent 70%)",
          filter: "blur(40px)",
        }}
      />
    </div>
  );
}

// ============================================
// 主组件
// ============================================

export default function Workspace4() {
  const currentWorkspace = useCockpit4Store((s) => s.currentWorkspace);
  const [launcherOpen, setLauncherOpen] = useState(false);

  // v6.39 游客全览：不再强制锁定 AI 超市，游客可自由切换全部工作区（数据由遮罩/加密锁保护）
  const currentRole = useUserStore((s) => s.currentUser?.role);
  const effectiveWorkspace: WorkspaceId = currentWorkspace;

  // 快捷键：Cmd/Ctrl + K 打开启动器（v6.39 游客禁用启动器——防止绕过入口访问页面）
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        const role = useUserStore.getState().currentUser?.role;
        if (role === "guest") return; // 游客禁用启动器
        e.preventDefault();
        setLauncherOpen((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // v6.39 官网入口直达：?tab=xxx 时切换工作区（geodso → GEO+DSO，其它 → 经营中心/workflow，具体 Tab 由 WorkflowWorkspace 解析）
  useEffect(() => {
    try {
      const q = new URLSearchParams(window.location.search);
      const t = q.get("tab");
      if (t) {
        useCockpit4Store.getState().setWorkspace(t === "geodso" ? "geodso" : "workflow");
      }
    } catch {
      // ignore
    }
  }, []);

  // 调试：暴露 RuntimeManager 和 store 到 window（仅开发环境，方便 Console 测试）
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).__rm = RuntimeManager;
      (window as any).__store = useCockpit4Store;
      (window as any).__testKnowledge = async () => {
        console.log('=== 测试 KnowledgeService ===');
        try {
          const result = await RuntimeManager.query(RuntimeName.Knowledge, {
            type: KnowledgeQuery.ListCards,
          });
          console.log('ListCards 结果:', {
            success: result.success,
            dataLength: Array.isArray(result.data) ? result.data.length : 'N/A',
            error: result.error,
            firstCard: Array.isArray(result.data) && result.data[0] ? result.data[0].title : 'N/A',
          });
          return result;
        } catch (err) {
          console.error('ListCards 异常:', err);
          throw err;
        }
      };
      (window as any).__testNews = async () => {
        console.log('=== 测试 NewsService ===');
        try {
          const result = await RuntimeManager.query(RuntimeName.News, {
            type: 'list_digests',
            limit: 7,
          });
          console.log('list_digests 结果:', {
            success: result.success,
            dataLength: Array.isArray(result.data) ? result.data.length : 'N/A',
            error: result.error,
          });
          return result;
        } catch (err) {
          console.error('list_digests 异常:', err);
          throw err;
        }
      };
      (window as any).__testContent = async () => {
        console.log('=== 测试 ContentService ===');
        try {
          const result = await RuntimeManager.query(RuntimeName.Content, {
            type: 'list_packages',
            limit: 5,
          });
          console.log('list_packages 结果:', {
            success: result.success,
            dataLength: Array.isArray(result.data) ? result.data.length : 'N/A',
            error: result.error,
          });
          return result;
        } catch (err) {
          console.error('list_packages 异常:', err);
          throw err;
        }
      };
      console.log('调试工具已就绪: window.__testKnowledge(), window.__testNews(), window.__testContent()');
    }
  }, []);

  const CurrentWorkspace = WORKSPACES[effectiveWorkspace];

  // v6.39 游客全览：遮罩&加密锁规则
  //   - guest + projects → AI 超市完全开放
  //   - guest + settings/finance → 加密锁（需登录解锁）
  //   - guest + 其他 → 毛玻璃预览遮罩
  const isGuest = currentRole === "guest";
  const needsEncryptionLock = isGuest && (effectiveWorkspace === "settings" || effectiveWorkspace === "finance");
  const needsPreviewOverlay = isGuest && !needsEncryptionLock && effectiveWorkspace !== "projects";

  return (
    <>
      <BackgroundEffects />
      {/* Phase3 P1-2：首次登录新手向导（全局，游客不触发） */}
      <FirstRunOnboarding />
      <div className="h-screen w-screen flex flex-col overflow-hidden">
        {/* ============ 顶部 Header ============ */}
        <WorkspaceHeader4 />

        {/* Phase3 P2：业务链路面包屑（总控台 > 业务主线 > 当前模块） */}
        <WorkspaceBizCrumb />

        {/* ============ 中部：Workspace + Hermes Drawer ============ */}
        <main className="flex-1 min-h-0 flex overflow-hidden">
          {/* 当前 Workspace 内容（全屏，不滚动，由 Workspace 内部自行处理滚动） */}
          <div className="flex-1 min-w-0 h-full overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={effectiveWorkspace}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                className="h-full w-full relative"
              >
                {/* 工作区级错误隔离：单个 Workspace 渲染异常不再让整页白屏，
                    仍可通过顶栏切换到其他工作区 */}
                <ErrorBoundary key={effectiveWorkspace} title={`${effectiveWorkspace} 渲染异常`}>
                  <CurrentWorkspace />
                </ErrorBoundary>

                {/* v6.39 游客全览数据保护层 */}
                {needsEncryptionLock && <WorkspaceAccessGate workspaceId={effectiveWorkspace} />}
                {needsPreviewOverlay && <GuestPreviewOverlay workspaceId={effectiveWorkspace} />}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Hermes 右侧抽屉（v6.39 游客隐藏 Hermes 抽屉——对话消耗 LLM 额度） */}
          {!isGuest && <HermesDrawer />}
        </main>

        {/* ============ 底部 StatusBar ============ */}
        <StatusBar4 />
      </div>

      {/* ============ AI 启动器模态框 (Cmd/Ctrl+K) — v6.39 游客禁用启动器（已快捷键拦截 + 去除启动器按钮） ============ */}
      {launcherOpen && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh] bg-black/60 backdrop-blur-sm"
          onClick={() => setLauncherOpen(false)}
        >
          <div onClick={(e) => e.stopPropagation()} className="w-full px-4">
            <AILauncherPanel onClose={() => setLauncherOpen(false)} />
          </div>
        </div>
      )}
    </>
  );
}
